/**
 * SNS共有ユーティリティ
 *
 * lecture.json の course.data_type / course.name を使い
 * 各SNSへの共有URL・文言を組み立てる。
 *
 * 共有方式:
 * - X / LINE: テキスト＋講座URL を各公式シェア API に渡してダイアログを開く
 * - Facebook: 講座URL のみを sharer.php に渡す（テキスト pre-fill 不可）
 */

import { getCachedFrontendSettings } from '../api/frontendSettings';

/**
 * フォーサイト公式サイトのドメインを取得する。
 * 管理画面の「外部ドメイン設定」で登録した値（frontend-settings API 経由・
 * LecturePage マウント時に取得済みのキャッシュ）を参照する。
 */
function getOfficialDomain(): string {
  return getCachedFrontendSettings()?.officialDomain ?? '';
}

/**
 * lecture.json の course.data_type から講座ページ（RTLページ）のURLを生成する。
 * 例: data_type="takken" → https://www.foresight.jp/takken/rtl/
 *
 * @param dataType - course.data_type（例: "takken", "fp"）
 */
export function getCourseShareUrl(dataType: string | null | undefined): string {
  const domain = getOfficialDomain();
  if (!dataType) return `https://${domain}/rtl/`;
  return `https://${domain}/${dataType}/rtl/`;
}

/**
 * SNS共有テキストを生成する。URLは含まない（各SNS共有関数・コピー処理で付与する）。
 *
 * @param courseName     - course.name（例: "宅建"）。ハッシュタグに使用
 * @param rankingTier    - 上位何%か（20 | 40 | null）。文言の出し分けに使用
 * @param includeHashtags - ハッシュタグを含めるか（LINE トークなどリンク化されない場合は false）
 */
export function buildShareText(
  courseName: string | null | undefined,
  rankingTier: 20 | 40 | null,
  includeHashtags = true,
): string {
  const now = new Date();
  const dateStr = `${now.getMonth() + 1}月${now.getDate()}日`;

  let resultText: string;
  if (rankingTier === 20) {
    resultText = `テスト結果は上位${rankingTier}%です🎉\nおめでとうございます👏`;
  } else if (rankingTier === 40) {
    resultText = `テスト結果は上位${rankingTier}%です🎉\nおめでとうございます👏`;
  } else {
    resultText = `またの参加をお待ちしております👏`;
  }

  const base = `${dateStr}にeライブスタディに参加しました。\n${resultText}`;
  if (!includeHashtags) return base;

  const hashtags = ['フォーサイトで勉強中', 'eライブスタディ'];
  if (courseName) hashtags.push(courseName);
  const hashtagStr = hashtags.map((t) => `#${t}`).join(' ');

  return `${base}\n${hashtagStr}`;
}

/** X（旧Twitter）共有ダイアログURLを生成（テキスト＋URL） */
export function buildXShareUrl(text: string, url: string): string {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
}

/**
 * Facebook共有ダイアログURLを生成（URLのみ）
 * Facebook の sharer.php はテキストの pre-fill を受け付けないため URL のみを渡す。
 * 共有先ページの OGP によってカード表示が決まる。
 */
export function buildFacebookShareUrl(shareUrl: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
}

/** LINE共有URLを生成（テキスト＋URL） */
export function buildLineShareUrl(shareUrl: string, text: string): string {
  return `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`;
}
